<template>
  <div class="flex flex-row items-center">
    <Datepicker
      v-model="date"
      :enableTimePicker="false"
      locale="ja"
      :hideInputIcon="false"
      weekStart="0"
      autoApply
      @update:modelValue="changeDate"
      :name="name"
      :format="formatDate"
    >
      <template #arrow-left>
        <arrow-icn
          width="20px"
          height="20px"
          :class="`${color}-icon`"
        ></arrow-icn>
      </template>
      <template #year="{ year }"> {{ year }}年 </template>

      <template #arrow-right>
        <arrow-icn
          width="20px"
          height="20px"
          :class="`${color}-icon`"
          class="transform rotate-180"
        ></arrow-icn>
      </template>
      <template #dp-input="{ value }">
        <div class="relative text-gray-600 focus-within:text-gray-400">
          <span class="absolute inset-y-0 left-0 flex items-center pl-2">
            <calendar-icn
              :class="`${color}-icon input-slot-image`"
            ></calendar-icn>
          </span>
          <input
            :class="
              `border border-${color} rounded-3xl h-10 py-2 pl-10 focus:outline-none text-black`
            "
            style="width: 160px"
            type="text"
            :value="value"
            :placeholder="placeholder"
            readonly
          />
        </div>
      </template>
      <template #calendar-header="{ index, day }">
        <div
          :class="
            index === new Date().getDay() ? 'current-weekday-datepicker' : ''
          "
        >
          {{ day }}
        </div>
      </template>
      <template #action-select></template>
      <template #action-preview></template>
    </Datepicker>

    <span :class="`text-lg text-${color} ml-1`" v-if="date">
      {{ $filters.wareki(date) }}
    </span>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import dayjs from 'dayjs'

import CalendarIcn from '@/assets/icons/calendar.svg?inline'
import ArrowIcn from '@/assets/icons/arrow.svg?inline'

export default {
  name: 'AppDateInput',
  components: { Datepicker, CalendarIcn, ArrowIcn },

  props: {
    placeholder: {
      type: String,
      default: '',
    },
    name: String,
    modelValue: Date,
    color: {
      type: String,
      default: '',
    },
  },

  emits: ['update:modelValue'],

  setup(props, context) {
    const date = ref()

    onMounted(() => {
      if (props.modelValue) {
        date.value = dayjs(props.modelValue).format('YYYY/MM/DD HH:mm:ss')
      }
      let css = ''
      switch (props.color) {
        case 'blue':
          css = `
            .dp__theme_light {
              --dp-background-color: #ffffff;
              --dp-text-color: #2993CE;
              --dp-hover-color: #2993CE20;
              --dp-hover-text-color: #2993CE;
              --dp-hover-icon-color: #2993CE;
              --dp-primary-color: #2993CE;
              --dp-primary-text-color: #f8f5f5;
              --dp-secondary-color: #c0c4cc;
              --dp-border-color: #2993CE;
              --dp-border-color-hover: #aaaeb7;
              --dp-disabled-color: #f6f6f6;
              --dp-scroll-bar-background: #2993CE20;
              --dp-scroll-bar-color: #2993CE;
              --dp-success-color: #76d275;
              --dp-icon-color: #2993CE;
              --dp-danger-color: #ff6f60;
            }`
          break
        case 'green':
          css = `
            .dp__theme_light {
              --dp-background-color: #ffffff;
              --dp-text-color: #549A1D;
              --dp-hover-color: #549A1D20;
              --dp-hover-text-color: #549A1D;
              --dp-hover-icon-color: #549A1D;
              --dp-primary-color: #549A1D;
              --dp-primary-text-color: #f8f5f5;
              --dp-secondary-color: #c0c4cc;
              --dp-border-color: #549A1D;
              --dp-border-color-hover: #aaaeb7;
              --dp-disabled-color: #f6f6f6;
              --dp-scroll-bar-background: #549A1D20;
              --dp-scroll-bar-color: #549A1D;
              --dp-success-color: #76d275;
              --dp-icon-color: #549A1D;
              --dp-danger-color: #ff6f60;
            }`
          break
        case 'yellow':
          css = `
            .dp__theme_light {
              --dp-background-color: #ffffff;
              --dp-text-color: #B8A000;
              --dp-hover-color: #B8A00020;
              --dp-hover-text-color: #B8A000;
              --dp-hover-icon-color: #B8A000;
              --dp-primary-color: #B8A000;
              --dp-primary-text-color: #f8f5f5;
              --dp-secondary-color: #c0c4cc;
              --dp-border-color: #B8A000;
              --dp-border-color-hover: #aaaeb7;
              --dp-disabled-color: #f6f6f6;
              --dp-scroll-bar-background: #B8A00020;
              --dp-scroll-bar-color: #B8A000;
              --dp-success-color: #76d275;
              --dp-icon-color: #B8A000;
              --dp-danger-color: #ff6f60;
            }`
          break
      }

      let styleTag = document.createElement('style')
      styleTag.appendChild(document.createTextNode(css))
      document.head.appendChild(styleTag)
    })

    const changeDate = () => {
      context.emit('update:modelValue', date.value)
    }

    const formatDate = date => {
      return dayjs(date).format('YYYY/MM/DD')
    }

    return {
      date,
      changeDate,
      formatDate,
    }
  },
}
</script>

<style lang="scss">
.input-slot-image {
  height: 20px;
  width: 20px;
}

.dp__today {
  border: none;
  border-radius: 999px;
  background: var(--dp-hover-color);
}

.dp__cell_inner.dp__pointer.dp__date_hover {
  &:hover {
    // color: theme("colors.blue.DEFAULT");
    // background: theme("colors.blue.lightest");
    border-radius: 999px;
  }
}

.dp__cell_inner.dp__pointer.dp__active_date {
  // background: theme("colors.blue.DEFAULT");
  border-radius: 999px;
  color: #fff;
}

.dp__calendar_item {
  // color: theme("colors.blue.DEFAULT");
  font-family: Noto Sans JP;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 26px;
}

.dp__calendar_header_item {
  // color: theme("colors.blue.DEFAULT");
  font-family: Noto Sans JP;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 22px;
}

.dp__calendar_header_separator {
  height: 0px;
}

.db__calendar_header {
  // border-top: 1px dashed theme("colors.blue.DEFAULT");
  border-top: 1px dashed;
  // border-bottom: 1px dashed theme("colors.blue.DEFAULT");
  border-bottom: 1px dashed;
}

.dp__arrow_top,
.dp__action_row {
  display: none;
}

.dp__menu.dp__theme_light {
  border-radius: 20px !important;
  border-color: none !important;
  padding: 4px 4px !important;
}
.dp__calendar_wrapper {
  border-radius: 20px !important;
  // border: 2px solid theme("colors.blue.DEFAULT") !important;
  // border: 2px solid !important;
  padding: 8px 8px !important;
}

.current-weekday-datepicker {
  border-radius: 100px;
  // border: 1px solid theme("colors.blue.DEFAULT");
  border: 1px solid;
  height: 28px;
  width: 28px;
}

.dp__month_year_select,
.dp__month_year_select {
  font-family: Noto Sans JP;
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 26px;
  // color: theme("colors.blue.DEFAULT");
  &:hover {
    // color: theme("colors.blue.DEFAULT");
    // background: theme("colors.blue.lightest");
  }
}

.dp__overlay_cell_active {
  // background: theme("colors.blue.DEFAULT");
  color: #fff;
  font-family: Noto Sans JP;
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
}

.dp__overlay_cell.dp__overlay_cell_pad {
  &:hover {
    // color: theme("colors.blue.DEFAULT");
    // background: theme("colors.blue.lightest");
  }
}
.dp__overlay_col {
  height: 40px;
}
.dp__overlay_cell_pad {
  padding: 0px 0px;
  border-radius: 22px;
}
</style>
